/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */


import {Directive, Input, OnChanges, Provider, SimpleChanges, forwardRef} from '@angular/core';
import {Observable} from 'rxjs/Observable';
import {AbstractControl} from '../model';
import {NG_VALIDATORS, Validators} from '../validators';

/** @experimental */
export type ValidationErrors = {
  [key: string]: any
};

/**
 * An interface that can be implemented by classes that can act as validators.
 *
 * ## Usage
 *
 * ```typescript
 * @Directive({
 *   selector: '[custom-validator]',
 *   providers: [{provide: NG_VALIDATORS, useExisting: CustomValidatorDirective, multi: true}]
 * })
 * class CustomValidatorDirective implements Validator {
 *   validate(c: Control): {[key: string]: any} {
 *     return {"custom": true};
 *   }
 * }
 * ```
 *
 * @stable
 */
export interface Validator {
  validate(c: AbstractControl): ValidationErrors|null;
  registerOnValidatorChange?(fn: () => void): void;
}

/** @experimental */
export interface AsyncValidator extends Validator {
  validate(c: AbstractControl): Promise<ValidationErrors|null>|Observable<ValidationErrors|null>;
}

export const /** @type {?} */ REQUIRED_VALIDATOR: Provider = {
  provide: NG_VALIDATORS,
  useExisting: forwardRef(() => RequiredValidator),
  multi: true
};

export const /** @type {?} */ CHECKBOX_REQUIRED_VALIDATOR: Provider = {
  provide: NG_VALIDATORS,
  useExisting: forwardRef(() => CheckboxRequiredValidator),
  multi: true
};
/**
 * A Directive that adds the `required` validator to any controls marked with the
 * `required` attribute, via the {\@link NG_VALIDATORS} binding.
 * 
 * ### Example
 * 
 * ```
 * <input name="fullName" ngModel required>
 * ```
 * 
 * \@stable
 */
export class RequiredValidator implements Validator {
private _required: boolean;
private _onChange: () => void;
/**
 * @return {?}
 */
get required(): boolean|string { return this._required; }
/**
 * @param {?} value
 * @return {?}
 */
set required(value: boolean|string) {
    this._required = value != null && value !== false && `${value}` !== 'false';
    if (this._onChange) this._onChange();
  }
/**
 * @param {?} c
 * @return {?}
 */
validate(c: AbstractControl): ValidationErrors|null {
    return this.required ? Validators.required(c) : null;
  }
/**
 * @param {?} fn
 * @return {?}
 */
registerOnValidatorChange(fn: () => void): void { this._onChange = fn; }
static decorators: DecoratorInvocation[] = [
{ type: Directive, args: [{
  selector:
      ':not([type=checkbox])[required][formControlName],:not([type=checkbox])[required][formControl],:not([type=checkbox])[required][ngModel]',
  providers: [REQUIRED_VALIDATOR],
  host: {'[attr.required]': 'required ? "" : null'}
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'required': [{ type: Input },],
};
}

function RequiredValidator_tsickle_Closure_declarations() {
/** @type {?} */
RequiredValidator.decorators;
/**
 * @nocollapse
 * @type {?}
 */
RequiredValidator.ctorParameters;
/** @type {?} */
RequiredValidator.propDecorators;
/** @type {?} */
RequiredValidator.prototype._required;
/** @type {?} */
RequiredValidator.prototype._onChange;
}

/**
 * A Directive that adds the `required` validator to checkbox controls marked with the
 * `required` attribute, via the {\@link NG_VALIDATORS} binding.
 * 
 * ### Example
 * 
 * ```
 * <input type="checkbox" name="active" ngModel required>
 * ```
 * 
 * \@experimental
 */
export class CheckboxRequiredValidator extends RequiredValidator {
/**
 * @param {?} c
 * @return {?}
 */
validate(c: AbstractControl): ValidationErrors|null {
    return this.required ? Validators.requiredTrue(c) : null;
  }
static decorators: DecoratorInvocation[] = [
{ type: Directive, args: [{
  selector:
      'input[type=checkbox][required][formControlName],input[type=checkbox][required][formControl],input[type=checkbox][required][ngModel]',
  providers: [CHECKBOX_REQUIRED_VALIDATOR],
  host: {'[attr.required]': 'required ? "" : null'}
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
];
}

function CheckboxRequiredValidator_tsickle_Closure_declarations() {
/** @type {?} */
CheckboxRequiredValidator.decorators;
/**
 * @nocollapse
 * @type {?}
 */
CheckboxRequiredValidator.ctorParameters;
}

/**
 * Provider which adds {\@link EmailValidator} to {\@link NG_VALIDATORS}.
 */
export const EMAIL_VALIDATOR: any = {
  provide: NG_VALIDATORS,
  useExisting: forwardRef(() => EmailValidator),
  multi: true
};
/**
 * A Directive that adds the `email` validator to controls marked with the
 * `email` attribute, via the {\@link NG_VALIDATORS} binding.
 * 
 * ### Example
 * 
 * ```
 * <input type="email" name="email" ngModel email>
 * <input type="email" name="email" ngModel email="true">
 * <input type="email" name="email" ngModel [email]="true">
 * ```
 * 
 * \@experimental
 */
export class EmailValidator implements Validator {
private _enabled: boolean;
private _onChange: () => void;
/**
 * @param {?} value
 * @return {?}
 */
set email(value: boolean|string) {
    this._enabled = value === '' || value === true || value === 'true';
    if (this._onChange) this._onChange();
  }
/**
 * @param {?} c
 * @return {?}
 */
validate(c: AbstractControl): ValidationErrors|null {
    return this._enabled ? Validators.email(c) : null;
  }
/**
 * @param {?} fn
 * @return {?}
 */
registerOnValidatorChange(fn: () => void): void { this._onChange = fn; }
static decorators: DecoratorInvocation[] = [
{ type: Directive, args: [{
  selector: '[email][formControlName],[email][formControl],[email][ngModel]',
  providers: [EMAIL_VALIDATOR]
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'email': [{ type: Input },],
};
}

function EmailValidator_tsickle_Closure_declarations() {
/** @type {?} */
EmailValidator.decorators;
/**
 * @nocollapse
 * @type {?}
 */
EmailValidator.ctorParameters;
/** @type {?} */
EmailValidator.propDecorators;
/** @type {?} */
EmailValidator.prototype._enabled;
/** @type {?} */
EmailValidator.prototype._onChange;
}


/**
 * @stable
 */
export interface ValidatorFn { (c: AbstractControl): ValidationErrors|null; }

/**
 * @stable
 */
export interface AsyncValidatorFn {
  (c: AbstractControl): Promise<ValidationErrors|null>|Observable<ValidationErrors|null>;
}
/**
 * Provider which adds {\@link MinLengthValidator} to {\@link NG_VALIDATORS}.
 * 
 * ## Example:
 * 
 * {\@example common/forms/ts/validators/validators.ts region='min'}
 */
export const MIN_LENGTH_VALIDATOR: any = {
  provide: NG_VALIDATORS,
  useExisting: forwardRef(() => MinLengthValidator),
  multi: true
};
/**
 * A directive which installs the {\@link MinLengthValidator} for any `formControlName`,
 * `formControl`, or control with `ngModel` that also has a `minlength` attribute.
 * 
 * \@stable
 */
export class MinLengthValidator implements Validator,
    OnChanges {
private _validator: ValidatorFn;
private _onChange: () => void;

   minlength: string;
/**
 * @param {?} changes
 * @return {?}
 */
ngOnChanges(changes: SimpleChanges): void {
    if ('minlength' in changes) {
      this._createValidator();
      if (this._onChange) this._onChange();
    }
  }
/**
 * @param {?} c
 * @return {?}
 */
validate(c: AbstractControl): ValidationErrors|null {
    return this.minlength == null ? null : this._validator(c);
  }
/**
 * @param {?} fn
 * @return {?}
 */
registerOnValidatorChange(fn: () => void): void { this._onChange = fn; }
/**
 * @return {?}
 */
private _createValidator(): void {
    this._validator = Validators.minLength(parseInt(this.minlength, 10));
  }
static decorators: DecoratorInvocation[] = [
{ type: Directive, args: [{
  selector: '[minlength][formControlName],[minlength][formControl],[minlength][ngModel]',
  providers: [MIN_LENGTH_VALIDATOR],
  host: {'[attr.minlength]': 'minlength ? minlength : null'}
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'minlength': [{ type: Input },],
};
}

function MinLengthValidator_tsickle_Closure_declarations() {
/** @type {?} */
MinLengthValidator.decorators;
/**
 * @nocollapse
 * @type {?}
 */
MinLengthValidator.ctorParameters;
/** @type {?} */
MinLengthValidator.propDecorators;
/** @type {?} */
MinLengthValidator.prototype._validator;
/** @type {?} */
MinLengthValidator.prototype._onChange;
/** @type {?} */
MinLengthValidator.prototype.minlength;
}

/**
 * Provider which adds {\@link MaxLengthValidator} to {\@link NG_VALIDATORS}.
 * 
 * ## Example:
 * 
 * {\@example common/forms/ts/validators/validators.ts region='max'}
 */
export const MAX_LENGTH_VALIDATOR: any = {
  provide: NG_VALIDATORS,
  useExisting: forwardRef(() => MaxLengthValidator),
  multi: true
};
/**
 * A directive which installs the {\@link MaxLengthValidator} for any `formControlName,
 * `formControl`,
 * or control with `ngModel` that also has a `maxlength` attribute.
 * 
 * \@stable
 */
export class MaxLengthValidator implements Validator,
    OnChanges {
private _validator: ValidatorFn;
private _onChange: () => void;

   maxlength: string;
/**
 * @param {?} changes
 * @return {?}
 */
ngOnChanges(changes: SimpleChanges): void {
    if ('maxlength' in changes) {
      this._createValidator();
      if (this._onChange) this._onChange();
    }
  }
/**
 * @param {?} c
 * @return {?}
 */
validate(c: AbstractControl): ValidationErrors|null {
    return this.maxlength != null ? this._validator(c) : null;
  }
/**
 * @param {?} fn
 * @return {?}
 */
registerOnValidatorChange(fn: () => void): void { this._onChange = fn; }
/**
 * @return {?}
 */
private _createValidator(): void {
    this._validator = Validators.maxLength(parseInt(this.maxlength, 10));
  }
static decorators: DecoratorInvocation[] = [
{ type: Directive, args: [{
  selector: '[maxlength][formControlName],[maxlength][formControl],[maxlength][ngModel]',
  providers: [MAX_LENGTH_VALIDATOR],
  host: {'[attr.maxlength]': 'maxlength ? maxlength : null'}
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'maxlength': [{ type: Input },],
};
}

function MaxLengthValidator_tsickle_Closure_declarations() {
/** @type {?} */
MaxLengthValidator.decorators;
/**
 * @nocollapse
 * @type {?}
 */
MaxLengthValidator.ctorParameters;
/** @type {?} */
MaxLengthValidator.propDecorators;
/** @type {?} */
MaxLengthValidator.prototype._validator;
/** @type {?} */
MaxLengthValidator.prototype._onChange;
/** @type {?} */
MaxLengthValidator.prototype.maxlength;
}



export const /** @type {?} */ PATTERN_VALIDATOR: any = {
  provide: NG_VALIDATORS,
  useExisting: forwardRef(() => PatternValidator),
  multi: true
};
/**
 * A Directive that adds the `pattern` validator to any controls marked with the
 * `pattern` attribute, via the {\@link NG_VALIDATORS} binding. Uses attribute value
 * as the regex to validate Control value against.  Follows pattern attribute
 * semantics; i.e. regex must match entire Control value.
 * 
 * ### Example
 * 
 * ```
 * <input [name]="fullName" pattern="[a-zA-Z ]*" ngModel>
 * ```
 * \@stable
 */
export class PatternValidator implements Validator,
    OnChanges {
private _validator: ValidatorFn;
private _onChange: () => void;

   pattern: string|RegExp;
/**
 * @param {?} changes
 * @return {?}
 */
ngOnChanges(changes: SimpleChanges): void {
    if ('pattern' in changes) {
      this._createValidator();
      if (this._onChange) this._onChange();
    }
  }
/**
 * @param {?} c
 * @return {?}
 */
validate(c: AbstractControl): ValidationErrors|null { return this._validator(c); }
/**
 * @param {?} fn
 * @return {?}
 */
registerOnValidatorChange(fn: () => void): void { this._onChange = fn; }
/**
 * @return {?}
 */
private _createValidator(): void { this._validator = Validators.pattern(this.pattern); }
static decorators: DecoratorInvocation[] = [
{ type: Directive, args: [{
  selector: '[pattern][formControlName],[pattern][formControl],[pattern][ngModel]',
  providers: [PATTERN_VALIDATOR],
  host: {'[attr.pattern]': 'pattern ? pattern : null'}
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'pattern': [{ type: Input },],
};
}

function PatternValidator_tsickle_Closure_declarations() {
/** @type {?} */
PatternValidator.decorators;
/**
 * @nocollapse
 * @type {?}
 */
PatternValidator.ctorParameters;
/** @type {?} */
PatternValidator.propDecorators;
/** @type {?} */
PatternValidator.prototype._validator;
/** @type {?} */
PatternValidator.prototype._onChange;
/** @type {?} */
PatternValidator.prototype.pattern;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
